import React, { useState } from 'react';
import { useSettings } from '../../contexts/SettingsContext';
import toast from 'react-hot-toast';
import SafeIcon from '../../common/SafeIcon';
import { FiExternalLink, FiEye, FiEyeOff, FiSave } from 'react-icons/fi';


const ApiSettings = () => {
  const { settings, updateSettings } = useSettings();
  const [showThoughtlyKey, setShowThoughtlyKey] = useState(false);
  const [showBrevoKey, setShowBrevoKey] = useState(false);
  const [formData, setFormData] = useState({
    thoughtlyApiKey: settings.thoughtlyApiKey || '',
    thoughtlyTeamId: settings.thoughtlyTeamId || '',
    brevoApiKey: settings.brevoApiKey || ''
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    updateSettings(formData);
    toast.success('API settings saved successfully');
  };

  const handleChange = (e) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  return (
    <div className="p-6">
      <div className="mb-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-2">API Configuration</h3>
        <p className="text-gray-600">
          Configure your API keys to enable AI calling and email automation features.
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Thoughtly API */}
        <div className="border border-gray-200 rounded-lg p-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h4 className="text-md font-medium text-gray-900">Thoughtly API</h4>
              <p className="text-sm text-gray-600">Required for AI-powered calling functionality</p>
            </div>
            <a
              href="https://thoughtly.co"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-1 text-blue-600 hover:text-blue-700 text-sm"
            >
              Get API Key
              <SafeIcon icon={FiExternalLink} className="text-xs" />
            </a>
          </div>

          <div className="relative">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              API Key
            </label>
            <div className="relative">
              <input
                type={showThoughtlyKey ? 'text' : 'password'}
                name="thoughtlyApiKey"
                value={formData.thoughtlyApiKey}
                onChange={handleChange}
                placeholder="Enter your Thoughtly API key"
                className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
              <button
                type="button"
                onClick={() => setShowThoughtlyKey(!showThoughtlyKey)}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                <SafeIcon icon={showThoughtlyKey ? FiEyeOff : FiEye} />
              </button>
            </div>
          </div>

          <div className="mt-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Team ID
            </label>
            <input
              type="text"
              name="thoughtlyTeamId"
              value={formData.thoughtlyTeamId}
              onChange={handleChange}
              placeholder="Enter your Thoughtly team ID"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
        </div>

        {/* Brevo API */}
        <div className="border border-gray-200 rounded-lg p-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h4 className="text-md font-medium text-gray-900">Brevo API</h4>
              <p className="text-sm text-gray-600">Required for email marketing and automation</p>
            </div>
            <a
              href="https://brevo.com"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-1 text-blue-600 hover:text-blue-700 text-sm"
            >
              Get API Key
              <SafeIcon icon={FiExternalLink} className="text-xs" />
            </a>
          </div>

          <div className="relative">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              API Key
            </label>
            <div className="relative">
              <input
                type={showBrevoKey ? 'text' : 'password'}
                name="brevoApiKey"
                value={formData.brevoApiKey}
                onChange={handleChange}
                placeholder="Enter your Brevo API key"
                className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
              <button
                type="button"
                onClick={() => setShowBrevoKey(!showBrevoKey)}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                <SafeIcon icon={showBrevoKey ? FiEyeOff : FiEye} />
              </button>
            </div>
          </div>
        </div>

        {/* Connection Status */}
        <div className="bg-gray-50 rounded-lg p-4">
          <h4 className="text-sm font-medium text-gray-900 mb-3">Connection Status</h4>
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600">Thoughtly API</span>
              <span className={`text-sm ${formData.thoughtlyApiKey && formData.thoughtlyTeamId ? 'text-green-600' : 'text-gray-400'}`}>
                {formData.thoughtlyApiKey && formData.thoughtlyTeamId ? '✓ Configured' : '○ Not configured'}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600">Brevo API</span>
              <span className={`text-sm ${formData.brevoApiKey ? 'text-green-600' : 'text-gray-400'}`}>
                {formData.brevoApiKey ? '✓ Configured' : '○ Not configured'}
              </span>
            </div>
          </div>
        </div>

        <div className="flex justify-end pt-6 border-t border-gray-200">
          <button
            type="submit"
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            <SafeIcon icon={FiSave} className="text-sm" />
            Save Settings
          </button>
        </div>
      </form>
    </div>
  );
};

export default ApiSettings;